import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { FormField } from '../../../components/forms/FormField';
import { PageSpinner } from '../../../components/PageSpinner';
import { adminApi, type AdminPermission } from '../../../features/admin/admin.api';
import { toDisplayMessage } from '../../../lib/error-message';
import styles from './AdminPortal.module.css';

export function AdminRoleDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const role = useQuery({
    queryKey: ['admin', 'roles', id],
    queryFn: () => adminApi.getRole(id),
  });

  const permissions = useQuery({
    queryKey: ['admin', 'permissions'],
    queryFn: () => adminApi.listPermissions(),
  });

  const [selected, setSelected] = useState<string[]>([]);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (role.data !== undefined) {
      setSelected(role.data.permissionCodes);
    }
  }, [role.data]);

  // Grouped by module so a reviewer can scan one area at a time instead of
  // reading forty-two flat checkboxes.
  const byModule = useMemo(() => {
    const groups = new Map<string, AdminPermission[]>();

    for (const permission of permissions.data ?? []) {
      const existing = groups.get(permission.module) ?? [];
      existing.push(permission);
      groups.set(permission.module, existing);
    }

    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [permissions.data]);

  const save = useMutation({
    mutationFn: () => adminApi.setRolePermissions(id, selected, reason),
    onSuccess: async () => {
      setReason('');
      await queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
    },
  });

  if (role.isPending) {
    return <PageSpinner label="Loading the role" />;
  }

  if (role.isError) {
    return <Alert tone="error">{toDisplayMessage(role.error)}</Alert>;
  }

  const isSuperAdmin = role.data.code === 'SUPER_ADMIN';

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>{role.data.name}</h1>
        <code>{role.data.code}</code>
      </header>

      <section className={styles.card} aria-labelledby="summary-heading">
        <h2 className={styles.cardTitle} id="summary-heading">
          Summary
        </h2>

        <dl className={styles.definitionList}>
          <dt>Description</dt>
          <dd>{role.data.description ?? '—'}</dd>

          <dt>Status</dt>
          <dd>{role.data.status}</dd>

          <dt>Protection</dt>
          <dd>{role.data.isSystem ? 'System role: cannot be deactivated' : 'Custom role'}</dd>

          <dt>Assigned to</dt>
          <dd>{role.data.assignedUserCount} users</dd>
        </dl>
      </section>

      <section className={styles.card} aria-labelledby="permissions-heading">
        <h2 className={styles.cardTitle} id="permissions-heading">
          Permissions
        </h2>

        {isSuperAdmin && (
          <Alert tone="info">
            SUPER_ADMIN always holds every permission. Its grants come from the seeded catalogue and
            cannot be edited here, so the role cannot be stripped of the access needed to restore
            it.
          </Alert>
        )}

        {save.isError && <Alert tone="error">{toDisplayMessage(save.error)}</Alert>}
        {save.isSuccess && <Alert tone="success">Permissions updated.</Alert>}

        <form
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          {byModule.map(([module, entries]) => (
            <fieldset className={styles.checkboxGrid} key={module}>
              <legend className={styles.legend}>{module}</legend>

              {entries.map((permission) => (
                <label className={styles.checkboxRow} key={permission.id}>
                  <input
                    type="checkbox"
                    disabled={isSuperAdmin}
                    checked={selected.includes(permission.code)}
                    onChange={(event) => {
                      setSelected((current) =>
                        event.target.checked
                          ? [...current, permission.code]
                          : current.filter((code) => code !== permission.code),
                      );
                    }}
                  />
                  <span>
                    {permission.name} <code>{permission.code}</code>
                  </span>
                </label>
              ))}
            </fieldset>
          ))}

          {!isSuperAdmin && (
            <>
              <FormField
                label="Reason for this change"
                name="reason"
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                }}
                hint="Recorded in the audit trail alongside the before and after grants."
                required
              />

              <div className={styles.actions}>
                <Button type="submit" busy={save.isPending}>
                  Save permissions
                </Button>
              </div>
            </>
          )}
        </form>
      </section>
    </div>
  );
}
